// UI strings for the optional creator-code field (CreatorCodeField, upgrade
// modal) and the signup survey's "Who told you about Solmi?" step
// (ReferrerNameField). Merged into the i18n `creatorCode` namespace
// via registerCreatorCodeI18n.ts. Languages missing here fall back to `en`.
// Server rules behind the messages live in server/creatorCodes.ts.

const creatorCodeTranslations = {
  en: {
    haveCode: "Have a creator code?",
    whoTitle: "Who told you about Solmi?",
    whoPlaceholder: "@handle, name or code",
    done: "Done",
    thanks: "Thanks for telling us.",
    placeholder: "Creator code",
    optional: "Optional",
    apply: "Apply",
    skip: "Skip",
    applied: "Code {{code}} applied.",
    appliedBonus: "Code {{code}} applied: +{{count}} video credits.",
    appliedDiscount:
      "Code {{code}} applied: {{percent}}% off your first purchase.",
    alreadyApplied: "Code {{code}} is already on your account.",
    unknownCode: "We don't recognize that code.",
    tooLate: "Creator codes can only be added in your first {{days}} days.",
    otherCode: "Your account already has a creator code.",
    rateLimited: "Too many tries. Please wait a few minutes.",
    error: "Couldn't apply the code. Please try again.",
  },
  es: {
    haveCode: "¿Tienes un código de creador?",
    whoTitle: "¿Quién te habló de Solmi?",
    whoPlaceholder: "@usuario, nombre o código",
    done: "Listo",
    thanks: "Gracias por contárnoslo.",
    placeholder: "Código de creador",
    optional: "Opcional",
    apply: "Aplicar",
    skip: "Omitir",
    applied: "Código {{code}} aplicado.",
    appliedBonus: "Código {{code}} aplicado: +{{count}} créditos de video.",
    appliedDiscount:
      "Código {{code}} aplicado: {{percent}}% de descuento en tu primera compra.",
    alreadyApplied: "El código {{code}} ya está en tu cuenta.",
    unknownCode: "No reconocemos ese código.",
    tooLate:
      "Los códigos de creador solo se pueden añadir en tus primeros {{days}} días.",
    otherCode: "Tu cuenta ya tiene un código de creador.",
    rateLimited: "Demasiados intentos. Espera unos minutos.",
    error: "No se pudo aplicar el código. Inténtalo de nuevo.",
  },
  ja: {
    haveCode: "クリエイターコードをお持ちですか？",
    whoTitle: "Solmiを誰から知りましたか？",
    whoPlaceholder: "@ハンドル、名前、またはコード",
    done: "完了",
    thanks: "教えていただきありがとうございます。",
    placeholder: "クリエイターコード",
    optional: "任意",
    apply: "適用",
    skip: "スキップ",
    applied: "コード {{code}} を適用しました。",
    appliedBonus: "コード {{code}} を適用しました：動画クレジット +{{count}}。",
    appliedDiscount:
      "コード {{code}} を適用しました：初回購入が {{percent}}% オフ。",
    alreadyApplied: "コード {{code}} はすでにアカウントに登録されています。",
    unknownCode: "このコードは認識できません。",
    tooLate: "クリエイターコードは登録から {{days}} 日以内のみ追加できます。",
    otherCode: "アカウントにはすでにクリエイターコードがあります。",
    rateLimited: "試行回数が多すぎます。数分後にもう一度お試しください。",
    error: "コードを適用できませんでした。もう一度お試しください。",
  },
  pt: {
    haveCode: "Tem um código de criador?",
    whoTitle: "Quem te falou da Solmi?",
    whoPlaceholder: "@perfil, nome ou código",
    done: "Pronto",
    thanks: "Obrigado por nos contar.",
    placeholder: "Código de criador",
    optional: "Opcional",
    apply: "Aplicar",
    skip: "Pular",
    applied: "Código {{code}} aplicado.",
    appliedBonus: "Código {{code}} aplicado: +{{count}} créditos de vídeo.",
    appliedDiscount:
      "Código {{code}} aplicado: {{percent}}% de desconto na sua primeira compra.",
    alreadyApplied: "O código {{code}} já está na sua conta.",
    unknownCode: "Não reconhecemos esse código.",
    tooLate:
      "Códigos de criador só podem ser adicionados nos seus primeiros {{days}} dias.",
    otherCode: "Sua conta já tem um código de criador.",
    rateLimited: "Muitas tentativas. Aguarde alguns minutos.",
    error: "Não foi possível aplicar o código. Tente novamente.",
  },
  fr: {
    haveCode: "Vous avez un code créateur ?",
    whoTitle: "Qui vous a parlé de Solmi ?",
    whoPlaceholder: "@pseudo, nom ou code",
    done: "Terminé",
    thanks: "Merci pour votre réponse.",
    placeholder: "Code créateur",
    optional: "Facultatif",
    apply: "Appliquer",
    skip: "Passer",
    applied: "Code {{code}} appliqué.",
    appliedBonus: "Code {{code}} appliqué : +{{count}} crédits vidéo.",
    appliedDiscount:
      "Code {{code}} appliqué : {{percent}} % de réduction sur votre premier achat.",
    alreadyApplied: "Le code {{code}} est déjà sur votre compte.",
    unknownCode: "Nous ne reconnaissons pas ce code.",
    tooLate:
      "Les codes créateur ne peuvent être ajoutés que pendant vos {{days}} premiers jours.",
    otherCode: "Votre compte a déjà un code créateur.",
    rateLimited: "Trop de tentatives. Patientez quelques minutes.",
    error: "Impossible d'appliquer le code. Réessayez.",
  },
  zh: {
    haveCode: "有创作者代码吗？",
    whoTitle: "是谁向你推荐了 Solmi？",
    whoPlaceholder: "@账号、名字或代码",
    done: "完成",
    thanks: "感谢告诉我们。",
    placeholder: "创作者代码",
    optional: "选填",
    apply: "使用",
    skip: "跳过",
    applied: "已使用代码 {{code}}。",
    appliedBonus: "已使用代码 {{code}}：+{{count}} 视频积分。",
    appliedDiscount: "已使用代码 {{code}}：首次购买立减 {{percent}}%。",
    alreadyApplied: "代码 {{code}} 已在你的账户中。",
    unknownCode: "无法识别该代码。",
    tooLate: "创作者代码仅可在注册后 {{days}} 天内添加。",
    otherCode: "你的账户已有创作者代码。",
    rateLimited: "尝试次数过多，请几分钟后再试。",
    error: "无法使用该代码，请重试。",
  },
  ko: {
    haveCode: "크리에이터 코드가 있으신가요?",
    whoTitle: "누구에게서 Solmi를 알게 되셨나요?",
    whoPlaceholder: "@핸들, 이름 또는 코드",
    done: "완료",
    thanks: "알려 주셔서 감사합니다.",
    placeholder: "크리에이터 코드",
    optional: "선택 사항",
    apply: "적용",
    skip: "건너뛰기",
    applied: "코드 {{code}}이(가) 적용되었습니다.",
    appliedBonus: "코드 {{code}} 적용 완료: 동영상 크레딧 +{{count}}.",
    appliedDiscount: "코드 {{code}} 적용 완료: 첫 구매 {{percent}}% 할인.",
    alreadyApplied: "코드 {{code}}이(가) 이미 계정에 등록되어 있습니다.",
    unknownCode: "인식할 수 없는 코드입니다.",
    tooLate:
      "크리에이터 코드는 가입 후 {{days}}일 이내에만 추가할 수 있습니다.",
    otherCode: "계정에 이미 크리에이터 코드가 있습니다.",
    rateLimited: "시도 횟수가 너무 많습니다. 몇 분 후 다시 시도해 주세요.",
    error: "코드를 적용하지 못했습니다. 다시 시도해 주세요.",
  },
  id: {
    haveCode: "Punya kode kreator?",
    whoTitle: "Siapa yang memberitahumu tentang Solmi?",
    whoPlaceholder: "@handle, nama, atau kode",
    done: "Selesai",
    thanks: "Terima kasih sudah memberi tahu kami.",
    placeholder: "Kode kreator",
    optional: "Opsional",
    apply: "Terapkan",
    skip: "Lewati",
    applied: "Kode {{code}} diterapkan.",
    appliedBonus: "Kode {{code}} diterapkan: +{{count}} kredit video.",
    appliedDiscount:
      "Kode {{code}} diterapkan: diskon {{percent}}% untuk pembelian pertamamu.",
    alreadyApplied: "Kode {{code}} sudah ada di akunmu.",
    unknownCode: "Kami tidak mengenali kode itu.",
    tooLate: "Kode kreator hanya bisa ditambahkan dalam {{days}} hari pertama.",
    otherCode: "Akunmu sudah punya kode kreator.",
    rateLimited: "Terlalu banyak percobaan. Tunggu beberapa menit.",
    error: "Kode tidak bisa diterapkan. Coba lagi.",
  },
  it: {
    haveCode: "Hai un codice creator?",
    whoTitle: "Chi ti ha parlato di Solmi?",
    whoPlaceholder: "@nome utente, nome o codice",
    done: "Fatto",
    thanks: "Grazie per avercelo detto.",
    placeholder: "Codice creator",
    optional: "Facoltativo",
    apply: "Applica",
    skip: "Salta",
    applied: "Codice {{code}} applicato.",
    appliedBonus: "Codice {{code}} applicato: +{{count}} crediti video.",
    appliedDiscount:
      "Codice {{code}} applicato: {{percent}}% di sconto sul tuo primo acquisto.",
    alreadyApplied: "Il codice {{code}} è già nel tuo account.",
    unknownCode: "Non riconosciamo questo codice.",
    tooLate:
      "I codici creator si possono aggiungere solo nei primi {{days}} giorni.",
    otherCode: "Il tuo account ha già un codice creator.",
    rateLimited: "Troppi tentativi. Attendi qualche minuto.",
    error: "Impossibile applicare il codice. Riprova.",
  },
  nl: {
    haveCode: "Heb je een creatorcode?",
    whoTitle: "Wie heeft je over Solmi verteld?",
    whoPlaceholder: "@handle, naam of code",
    done: "Klaar",
    thanks: "Bedankt dat je het ons vertelt.",
    placeholder: "Creatorcode",
    optional: "Optioneel",
    apply: "Toepassen",
    skip: "Overslaan",
    applied: "Code {{code}} toegepast.",
    appliedBonus: "Code {{code}} toegepast: +{{count}} videocredits.",
    appliedDiscount:
      "Code {{code}} toegepast: {{percent}}% korting op je eerste aankoop.",
    alreadyApplied: "Code {{code}} staat al op je account.",
    unknownCode: "We herkennen deze code niet.",
    tooLate:
      "Creatorcodes kun je alleen in je eerste {{days}} dagen toevoegen.",
    otherCode: "Je account heeft al een creatorcode.",
    rateLimited: "Te veel pogingen. Wacht een paar minuten.",
    error: "De code kon niet worden toegepast. Probeer het opnieuw.",
  },
  pl: {
    haveCode: "Masz kod od twórcy?",
    whoTitle: "Kto powiedział Ci o Solmi?",
    whoPlaceholder: "@nazwa, imię lub kod",
    done: "Gotowe",
    thanks: "Dziękujemy za informację.",
    placeholder: "Kod twórcy",
    optional: "Opcjonalnie",
    apply: "Zastosuj",
    skip: "Pomiń",
    applied: "Kod {{code}} został zastosowany.",
    appliedBonus: "Kod {{code}} zastosowany: +{{count}} kredytów wideo.",
    appliedDiscount:
      "Kod {{code}} zastosowany: {{percent}}% zniżki na pierwszy zakup.",
    alreadyApplied: "Kod {{code}} jest już na Twoim koncie.",
    unknownCode: "Nie rozpoznajemy tego kodu.",
    tooLate: "Kody twórców można dodać tylko w ciągu pierwszych {{days}} dni.",
    otherCode: "Twoje konto ma już kod twórcy.",
    rateLimited: "Zbyt wiele prób. Odczekaj kilka minut.",
    error: "Nie udało się zastosować kodu. Spróbuj ponownie.",
  },
  tr: {
    haveCode: "İçerik üreticisi kodunuz var mı?",
    whoTitle: "Solmi'yi size kim önerdi?",
    whoPlaceholder: "@kullanıcı adı, isim veya kod",
    done: "Tamam",
    thanks: "Bize bildirdiğiniz için teşekkürler.",
    placeholder: "İçerik üreticisi kodu",
    optional: "İsteğe bağlı",
    apply: "Uygula",
    skip: "Atla",
    applied: "{{code}} kodu uygulandı.",
    appliedBonus: "{{code}} kodu uygulandı: +{{count}} video kredisi.",
    appliedDiscount:
      "{{code}} kodu uygulandı: ilk satın alımınızda %{{percent}} indirim.",
    alreadyApplied: "{{code}} kodu zaten hesabınızda.",
    unknownCode: "Bu kodu tanımıyoruz.",
    tooLate:
      "İçerik üreticisi kodları yalnızca ilk {{days}} gün içinde eklenebilir.",
    otherCode: "Hesabınızda zaten bir içerik üreticisi kodu var.",
    rateLimited: "Çok fazla deneme. Lütfen birkaç dakika bekleyin.",
    error: "Kod uygulanamadı. Lütfen tekrar deneyin.",
  },
  vi: {
    haveCode: "Bạn có mã nhà sáng tạo?",
    whoTitle: "Ai đã giới thiệu Solmi cho bạn?",
    whoPlaceholder: "@tên tài khoản, tên hoặc mã",
    done: "Xong",
    thanks: "Cảm ơn bạn đã cho chúng tôi biết.",
    placeholder: "Mã nhà sáng tạo",
    optional: "Không bắt buộc",
    apply: "Áp dụng",
    skip: "Bỏ qua",
    applied: "Đã áp dụng mã {{code}}.",
    appliedBonus: "Đã áp dụng mã {{code}}: +{{count}} credit video.",
    appliedDiscount:
      "Đã áp dụng mã {{code}}: giảm {{percent}}% cho lần mua đầu tiên.",
    alreadyApplied: "Mã {{code}} đã có trong tài khoản của bạn.",
    unknownCode: "Chúng tôi không nhận ra mã này.",
    tooLate: "Chỉ có thể thêm mã nhà sáng tạo trong {{days}} ngày đầu tiên.",
    otherCode: "Tài khoản của bạn đã có mã nhà sáng tạo.",
    rateLimited: "Thử quá nhiều lần. Vui lòng đợi vài phút.",
    error: "Không thể áp dụng mã. Vui lòng thử lại.",
  },
  de: {
    haveCode: "Hast du einen Creator-Code?",
    whoTitle: "Wer hat dir von Solmi erzählt?",
    whoPlaceholder: "@Handle, Name oder Code",
    done: "Fertig",
    thanks: "Danke für deine Antwort.",
    placeholder: "Creator-Code",
    optional: "Optional",
    apply: "Einlösen",
    skip: "Überspringen",
    applied: "Code {{code}} eingelöst.",
    appliedBonus: "Code {{code}} eingelöst: +{{count}} Video-Credits.",
    appliedDiscount:
      "Code {{code}} eingelöst: {{percent}} % Rabatt auf deinen ersten Kauf.",
    alreadyApplied: "Der Code {{code}} ist bereits in deinem Konto.",
    unknownCode: "Diesen Code kennen wir nicht.",
    tooLate:
      "Creator-Codes kannst du nur in deinen ersten {{days}} Tagen hinzufügen.",
    otherCode: "Dein Konto hat bereits einen Creator-Code.",
    rateLimited: "Zu viele Versuche. Bitte warte ein paar Minuten.",
    error:
      "Der Code konnte nicht eingelöst werden. Bitte versuche es noch einmal.",
  },
} as const;

export default creatorCodeTranslations;
